// PathCreationTableView — TreeTable view for lesson path selection.
// Ported from admin-v2/src/components/RecommendedAssignment/PathCreationTableView.jsx
// Logic unchanged — TypeScript only.

import { useMemo, useState, useCallback, useEffect, type CSSProperties } from "react";
import classNames from "classnames";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { useLocalStorage } from "primereact/hooks";
import type { TreeNode } from "primereact/treenode";
import type {
  TreeTableEvent,
  TreeTableSortEvent,
  TreeTableTogglerTemplateOptions,
  TreeTableFilterMeta,
} from "primereact/treetable";

import { getNodeLevel, type LessonNode } from "@/api/recommendedAssignments";
import { sortTreeNodes, type SortableNode } from "@/helpers/assignmentHelpers";
import {
  getStaticColumns,
  getConfigurableColumns,
  getInitialFilters,
  type ColumnDef,
} from "./PathCreationTableView.columns";
import styles from "./_PathCreationTableView.module.scss";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PathCreationTableViewProps {
  treeData?: LessonNode[];
  selectedKeys?: Record<string, { checked: boolean; partialChecked: boolean }>;
  onSelectionChange: (e: { value: unknown }) => void;
  onShowMoreClick: (item: LessonNode) => void;
  className?: string;
}

interface TableNodeData {
  name: string;
  proficiency: string;
  category: string;
  topic: string;
  type: string;
  duration: string;
  score: string | number;
  level: string;
  originalNode: LessonNode;
}

interface TableNode {
  key: string;
  data: TableNodeData;
  children?: TableNode[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export const PathCreationTableView = ({
  treeData = [],
  selectedKeys = {},
  onSelectionChange,
  onShowMoreClick,
  className = "",
}: PathCreationTableViewProps) => {
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState<0 | 1 | -1 | null>(1);
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});

  const tableData = useMemo<TableNode[]>(() => {
    if (!treeData || treeData.length === 0) return [];

    const transformNode = (
      node: LessonNode,
      proficiencyLabel = "",
      categoryLabel = "",
      topicLabel = ""
    ): TableNode => {
      const nodeData: TableNodeData = {
        name: node.label ?? "",
        proficiency: proficiencyLabel,
        category: categoryLabel,
        topic: topicLabel,
        type: (node as { lessonType?: string }).lessonType ?? "",
        duration: node.duration ? `${node.duration} min` : "",
        score: node.averageScore ?? "",
        level: getNodeLevel(node),
        originalNode: node,
      };

      const result: TableNode = {
        key: String(node.key ?? ""),
        data: nodeData,
      };

      if (node.children && node.children.length > 0) {
        const level = getNodeLevel(node);
        result.children = node.children.map(child =>
          transformNode(
            child,
            level === "proficiency" ? (node.label ?? "") : proficiencyLabel,
            level === "category" ? (node.label ?? "") : categoryLabel,
            level === "topic" ? (node.label ?? "") : topicLabel
          )
        );
      }

      return result;
    };

    return treeData.map(proficiencyLevel => transformNode(proficiencyLevel));
  }, [treeData]);

  const sortedTableData = useMemo<TableNode[]>(() => {
    if (!sortField || !tableData || tableData.length === 0) return tableData;
    return sortTreeNodes(
      tableData as unknown as SortableNode[],
      sortField,
      sortOrder ?? 1
    ) as unknown as TableNode[];
  }, [tableData, sortField, sortOrder]);

  const staticColumns = useMemo(() => getStaticColumns(), []);
  const configurableColumns = useMemo(() => getConfigurableColumns(), []);

  const [storedVisibleColumns, setStoredVisibleColumns] = useLocalStorage<string[]>(
    ["topic", "type", "score"],
    "path-creation-table-columns"
  );

  const visibleColumns = useMemo<ColumnDef[]>(
    () => configurableColumns.filter(col => storedVisibleColumns.includes(col.field)),
    [configurableColumns, storedVisibleColumns]
  );

  const onColumnSelectChange = useCallback(
    (e: { value: string[] }) => {
      setStoredVisibleColumns(e.value);
    },
    [setStoredVisibleColumns]
  );

  const allColumns = useMemo<ColumnDef[]>(
    () => [...staticColumns, ...visibleColumns],
    [staticColumns, visibleColumns]
  );

  const [filters, setFilters] = useState<Record<string, { value: unknown; matchMode: string }>>(
    () => ({ global: { value: null, matchMode: "contains" } })
  );

  useEffect(() => {
    setFilters(getInitialFilters(allColumns));
  }, [allColumns]);

  // Expand all non-proficiency nodes by default
  useEffect(() => {
    if (!tableData || tableData.length === 0) return;

    const collectAllKeys = (nodes: TableNode[], keys: Record<string, boolean> = {}) => {
      nodes.forEach(node => {
        if (node.key) keys[node.key] = true;
        if (node.children && node.children.length > 0) {
          collectAllKeys(node.children, keys);
        }
      });
      return keys;
    };

    const allKeys: Record<string, boolean> = {};
    tableData.forEach(proficiencyLevel => {
      if (proficiencyLevel.children && proficiencyLevel.children.length > 0) {
        collectAllKeys(proficiencyLevel.children, allKeys);
      }
    });

    setExpandedKeys(allKeys);
  }, [tableData]);

  const handleRowClick = (event: TreeTableEvent) => {
    const target = (event.originalEvent as unknown as MouseEvent).target as HTMLElement | null;
    const isCheckboxClick =
      target &&
      (target.closest(".p-checkbox") != null ||
        target.closest(".p-treetable-checkbox") != null ||
        target.classList.contains("p-checkbox") ||
        target.classList.contains("p-checkbox-box") ||
        target.classList.contains("p-checkbox-icon"));

    if (isCheckboxClick) return;

    const node = event.node as TableNode;
    const { data } = node;
    if (data.level === "lesson" && data.originalNode?.description) {
      onShowMoreClick(data.originalNode);
    }
  };

  const rowClassName = (node: TreeNode): Record<string, boolean> => {
    const data = (node as TableNode).data;
    return {
      "tree-row-clickable": data.level === "lesson",
      "tree-row-hover": true,
      "tree-row-category": data.level === "category",
      "tree-row-topic": data.level === "topic",
      "tree-row-lesson": data.level === "lesson",
    };
  };

  const clearAllFilters = useCallback(() => {
    setFilters(getInitialFilters(allColumns));
    setGlobalFilterValue("");
  }, [allColumns]);

  const renderFilterSummary = () => {
    const activeFilters = Object.entries(filters).filter(
      ([key, filter]) =>
        filter.value !== null &&
        filter.value !== undefined &&
        key !== "global" &&
        !(Array.isArray(filter.value) && filter.value.length === 0)
    );

    if (activeFilters.length === 0) return null;

    return (
      <div className={`flex items-center gap-2 body-sm ${themeClasses.textGray600}`}>
        <i className="pi pi-filter" />
        <span>
          {activeFilters.length} filter{activeFilters.length > 1 ? "s" : ""} active
        </span>
        <Button
          icon="pi pi-times"
          size="small"
          text
          onClick={clearAllFilters}
          tooltip="Clear all filters"
        />
      </div>
    );
  };

  const handleSort = (event: TreeTableSortEvent) => {
    setSortField(event.sortField ?? "");
    setSortOrder(event.sortOrder as 0 | 1 | -1 | null);
  };

  const togglerTemplate = (node: TreeNode | null, options: TreeTableTogglerTemplateOptions) => {
    if (!node) return null;

    const expanded = options.expanded;
    const iconClassName = classNames("p-treetable-toggler-icon pi pi-fw", {
      "pi-angle-right": !expanded,
      "pi-angle-down": expanded,
    });

    return (
      <button
        type="button"
        className="p-treetable-toggler"
        style={options.buttonStyle}
        tabIndex={-1}
        onClick={options.onClick}
      >
        <span className={iconClassName} aria-hidden="true" />
      </button>
    );
  };

  const renderProficiencyTable = (proficiencyData: TableNode) => {
    if (!proficiencyData || !proficiencyData.children?.length) return null;

    return (
      <div key={proficiencyData.key} className="mb-6">
        <h4 className="my-4 atlas-h3">{proficiencyData.data.name}</h4>

        <TreeTable
          value={proficiencyData.children as unknown as TreeNode[]}
          selectionMode="checkbox"
          selectionKeys={selectedKeys}
          onSelectionChange={onSelectionChange}
          expandedKeys={expandedKeys}
          onToggle={e => setExpandedKeys(e.value as Record<string, boolean>)}
          className="p-treetable-sm [&_.p-column-filter.p-inputtext.p-component]:max-w-full [&_.p-column-filter.p-inputtext.p-component]:w-full [&_.p-column-filter.p-inputtext.p-component]:box-border"
          onRowClick={handleRowClick}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={handleSort}
          rowClassName={rowClassName}
          filters={filters as unknown as TreeTableFilterMeta}
          onFilter={e =>
            setFilters(
              e.filters as unknown as Record<string, { value: unknown; matchMode: string }>
            )
          }
          globalFilter={globalFilterValue}
          resizableColumns
          togglerTemplate={togglerTemplate}
          style={{ "--selected-row-bg": "transparent" } as CSSProperties}
        >
          {allColumns.map(col => (
            <Column
              key={col.field}
              field={col.field}
              header={col.header}
              expander={col.expander}
              body={col.body}
              sortable={col.sortable}
              filter={col.filter}
              filterMatchMode={col.filterMatchMode}
              filterPlaceholder={col.filterPlaceholder}
              filterMaxLength={col.filterMaxLength}
              filterMenuStyle={col.filterMenuStyle}
              filterType={col.filterType}
              showClearButton={col.filter}
              style={col.style}
            />
          ))}
        </TreeTable>
      </div>
    );
  };

  if (!tableData || tableData.length === 0) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <p className={themeClasses.textGray500}>No lessons found</p>
      </div>
    );
  }

  const renderHeader = () => (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search" />
            <InputText
              value={globalFilterValue}
              onChange={e => setGlobalFilterValue(e.target.value)}
              placeholder="Search lessons..."
              autoComplete="off"
            />
          </IconField>

          <MultiSelect
            value={storedVisibleColumns}
            onChange={onColumnSelectChange}
            options={configurableColumns}
            optionLabel="header"
            optionValue="field"
            placeholder="Select columns"
            className="w-64"
            maxSelectedLabels={2}
            filter
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            icon="pi pi-filter-slash"
            label="Clear Filters"
            size="small"
            severity="secondary"
            onClick={clearAllFilters}
            tooltip="Clear all filters"
          />
        </div>
      </div>

      {renderFilterSummary()}
    </div>
  );

  return (
    <div className={`${styles.pathCreationTableView} ${className}`}>
      {renderHeader()}
      {sortedTableData.map(proficiencyLevel => renderProficiencyTable(proficiencyLevel))}
    </div>
  );
};
